import React, { useEffect, useMemo, useState } from 'react';
import AppHeader from './components/AppHeader';
import AdminSidebar from './components/AdminSidebar';
import AdminHome from './components/AdminHome';
import AdminDashboard from './components/AdminDashboard';
import SubmissionReviewQueue from './components/SubmissionReviewQueue';
import QuestionnaireBuilder from './components/QuestionnaireBuilder';
import QuestionnairesPage from './components/QuestionnairesPage';
import ClientPlanner from './components/ClientPlanner';
import ClientPlannerSync from './components/ClientPlannerSync';
import AuthScreen from './components/AuthScreen';
import EventDetail from './components/EventDetail';
import AppSettings from './components/AppSettings';
import {
  getClientQuestionnaireSections,
  getQuestionnaireSections
} from './utils/questionnaireSections';
import {
  createClientShareToken,
  deleteEventRecords,
  fetchClientEvent,
  loadOwnedEvents,
  saveEventRecord
} from './utils/eventService';
import { formatEventDate } from './utils/eventDisplay';
import { getQuestionnaireTemplates } from './utils/questionnaireTemplateStorage';
import supabase from './supabase/supabase';
import './App.css';
import './admin-layout-fixes.css';
import './components/auth.css';
import './portal-redesign.css';
import './theme.css';
import './components/admin-sidebar-logout.css';

const EVENTS_STORAGE_KEY = 'amp-events';
const templateAssignmentsInProgress = new Set();
const defaultSettings = {
  appName: 'ANGELS MUSIC PRODUCTIONS',
  logo: '',
  phone: '949-394-2572',
  email: 'info@angelsmusic.net'
};

function cloneSections(sections) {
  return Array.isArray(sections) ? JSON.parse(JSON.stringify(sections)) : [];
}

function cloneEvents(events) {
  return Array.isArray(events) ? JSON.parse(JSON.stringify(events)) : [];
}

function readStoredEvents() {
  try {
    const stored = localStorage.getItem(EVENTS_STORAGE_KEY);
    if (!stored) return cloneEvents([]);
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : cloneEvents([]);
  } catch {
    return cloneEvents([]);
  }
}

function readSettings() {
  try {
    return {
      ...defaultSettings,
      ...JSON.parse(localStorage.getItem('amp-app-settings') || '{}')
    };
  } catch {
    return defaultSettings;
  }
}

function getEventSections(event) {
  if (Array.isArray(event?.questionnaireSections)) {
    return getClientQuestionnaireSections(
      cloneSections(event.questionnaireSections),
      event.type || 'Wedding'
    );
  }
  return getQuestionnaireSections(event?.type || 'Wedding');
}

function getTemplateCategory(eventType) {
  const normalizedType = String(eventType || '').toLowerCase();

  if (normalizedType.includes('mitzvah')) {
    return {
      label: 'Bar/Bat Mitzvah',
      names: ['bar bat mitzvah']
    };
  }
  if (normalizedType.includes('wedding')) {
    return { label: 'Wedding', names: ['wedding'] };
  }
  if (normalizedType.includes('corporate')) {
    return {
      label: 'Corporate',
      names: ['corporate', 'corporate event']
    };
  }
  if (normalizedType.includes('birthday')) {
    return { label: 'Birthday', names: ['birthday'] };
  }
  return null;
}

function findTemplateForEventType(templates, eventType) {
  const category = getTemplateCategory(eventType);
  if (!category) return { category: null, template: null };

  const template = templates.find((item) => {
    const name = String(item.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    return category.names.includes(name);
  }) || null;

  return { category, template };
}

function hasMeaningfulAnswer(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulAnswer);
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([key]) => key !== 'dataUrl')
      .some(([, item]) => hasMeaningfulAnswer(item));
  }
  return false;
}

function hasClientStartedAnswering(event) {
  if (Number(event?.completion) > 0) return true;

  const payload = event?.clientAnswers;
  if (!payload || typeof payload !== 'object') return false;
  if (hasMeaningfulAnswer(payload.answers)) return true;

  if (!Object.prototype.hasOwnProperty.call(payload, 'answers') &&
      hasMeaningfulAnswer(payload)) {
    return true;
  }

  const timelineHasAnswers = Array.isArray(payload.timeline) &&
    payload.timeline.some((item) =>
      ['time', 'description', 'music', 'notes']
        .some((key) => hasMeaningfulAnswer(item?.[key]))
    );
  const songsHaveAnswers = Array.isArray(payload.songs) &&
    payload.songs.some((item) =>
      ['title', 'artist', 'notes', 'dedication']
        .some((key) => hasMeaningfulAnswer(item?.[key]))
    );
  const playlistsHaveAnswers = Array.isArray(payload.playlists) &&
    payload.playlists.some((item) =>
      hasMeaningfulAnswer(item?.url || item?.link || item)
    );

  return timelineHasAnswers || songsHaveAnswers || playlistsHaveAnswers;
}

function parseClientHash() {
  const match = window.location.hash.match(/^#client\/(.+)$/);
  if (!match) return { token: '', legacyEvent: null };
  const value = match[1];
  if (/^[a-f0-9-]{32,}$/i.test(value)) {
    return { token: value, legacyEvent: null };
  }
  try {
    return { token: '', legacyEvent: JSON.parse(decodeURIComponent(atob(value))) };
  } catch {
    return { token: '', legacyEvent: null };
  }
}

function findStoredEvent(eventId) {
  if (eventId === undefined || eventId === null) return null;
  return readStoredEvents().find((event) => String(event.id) === String(eventId)) || null;
}

function hydrateClientStorage(event, state) {
  const eventId = event.id;
  const values = {
    [`amp-planner-answers-${eventId}`]: state.answers || {},
    [`amp-timeline-${eventId}`]: state.timeline || [],
    [`amp-songs-${eventId}`]: state.songs || [
      { id: 1, title: '', artist: '', priority: 'Must Play', notes: '' }
    ],
    [`amp-playlists-${eventId}`]: state.playlists || []
  };
  Object.entries(values).forEach(([key, value]) => {
    localStorage.setItem(key, JSON.stringify(value));
  });
}

function App() {
  const clientHash = useMemo(parseClientHash, []);
  const legacyStoredEvent = clientHash.legacyEvent
    ? findStoredEvent(clientHash.legacyEvent.id)
    : null;
  const initialClientEvent = legacyStoredEvent || clientHash.legacyEvent;
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [mode, setMode] = useState(
    clientHash.token || initialClientEvent ? 'client' : 'admin'
  );
  const [page, setPage] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [sections, setSections] = useState(() => getEventSections(initialClientEvent));
  const [settings, setSettings] = useState(readSettings);
  const [selectedEvent, setSelectedEvent] = useState(initialClientEvent);
  const [clientEvent, setClientEvent] = useState(clientHash.token ? null : initialClientEvent);
  const [clientError, setClientError] = useState('');
  const [dataError, setDataError] = useState('');
  const [questionnaireTab, setQuestionnaireTab] = useState('templates');
  const [monthFilter, setMonthFilter] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) setDataError(error.message);
      setUser(data?.session?.user || null);
      setAuthReady(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      setAuthReady(true);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!clientHash.token) return undefined;
    let active = true;
    fetchClientEvent(clientHash.token)
      .then((result) => {
        if (!active) return;
        if (!result) {
          setClientError('This client link is invalid or the event is unavailable.');
          return;
        }
        const event = result.event;
        event.clientAnswers = result.answers;
        hydrateClientStorage(event, result.answers);
        setClientEvent(event);
        setSections(getEventSections(event));
        setClientError('');
      })
      .catch((error) => {
        if (active) setClientError(error.message || 'The client event could not be loaded.');
      });
    return () => { active = false; };
  }, [clientHash.token]);

  useEffect(() => {
    if (!user || mode === 'client') return undefined;
    let active = true;
    setEventsLoading(true);
    setDataError('');
    loadOwnedEvents(user.id)
      .then((nextEvents) => {
        if (!active) return;
        setEvents(nextEvents);
        setSelectedEvent((current) => current
          ? nextEvents.find((event) => String(event.id) === String(current.id)) || null
          : null);
      })
      .catch((error) => {
        if (active) setDataError(error.message || 'Events could not be loaded from Supabase.');
      })
      .finally(() => {
        if (active) setEventsLoading(false);
      });
    return () => { active = false; };
  }, [user, mode]);

  useEffect(() => {
    if (!user || mode === 'client') return undefined;
    const refresh = () => {
      loadOwnedEvents(user.id)
        .then(setEvents)
        .catch((error) => setDataError(error.message || 'Event refresh failed.'));
    };
    const timer = window.setInterval(refresh, 12000);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [user, mode]);

  useEffect(() => {
    if (!user || mode !== 'admin' || events.length === 0) return undefined;
    let active = true;
    const templates = getQuestionnaireTemplates();

    events.forEach((event) => {
      if (event.questionnaireTemplateId || hasClientStartedAnswering(event)) return;

      const { category, template } = findTemplateForEventType(templates, event.type);
      if (!category) return;
      if (!template) {
        setDataError(`The ${category.label} questionnaire template does not exist. Add it in Questionnaires to assign it.`);
        return;
      }

      const assignmentKey = `${user.id}:${event.id}`;
      if (templateAssignmentsInProgress.has(assignmentKey)) return;
      templateAssignmentsInProgress.add(assignmentKey);

      const updatedEvent = {
        ...event,
        questionnaireTemplateId: template.id,
        questionnaireSections: getClientQuestionnaireSections(
          cloneSections(template.sections),
          event.type
        )
      };

      saveEventRecord(user.id, updatedEvent)
        .then((savedEvent) => {
          if (!active) return;
          setEvents((current) => current.map((currentEvent) =>
            String(currentEvent.id) === String(savedEvent.id)
              ? savedEvent
              : currentEvent
          ));
          setSelectedEvent((current) =>
            current && String(current.id) === String(savedEvent.id)
              ? savedEvent
              : current
          );
        })
        .catch((error) => {
          if (active) setDataError(error.message || 'The questionnaire template could not be saved.');
        })
        .finally(() => {
          templateAssignmentsInProgress.delete(assignmentKey);
        });
    });

    return () => { active = false; };
  }, [events, user, mode]);

  useEffect(() => {
    const editingEventQuestionnaire = page === 'builder';
    const editingTemplatesBuilder = page === 'questionnaires' &&
      questionnaireTab === 'builder';
    if (
      mode !== 'admin' ||
      (!editingEventQuestionnaire && !editingTemplatesBuilder) ||
      !user ||
      !selectedEvent
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const updated = { ...selectedEvent, questionnaireSections: cloneSections(sections) };
      setSelectedEvent(updated);
      setEvents((current) => current.map((event) =>
        String(event.id) === String(updated.id) ? updated : event
      ));
      saveEventRecord(user.id, updated)
        .then((savedEvent) => {
          setSelectedEvent(savedEvent);
          setEvents((current) => current.map((event) =>
            String(event.id) === String(savedEvent.id) ? savedEvent : event
          ));
        })
        .catch((error) => setDataError(error.message || 'Questionnaire changes could not be saved.'));
    }, 700);
    return () => window.clearTimeout(timer);
  }, [sections, mode, page, questionnaireTab, user, selectedEvent]);

  const changeMode = (nextMode) => {
    if (nextMode === 'client' && !clientHash.token) {
      const previewEvent = selectedEvent || events[0];
      if (!previewEvent) {
        setDataError('Create or open an event before starting client preview.');
        return;
      }
      setSelectedEvent(previewEvent);
      setSections(getEventSections(previewEvent));
    }
    setMode(nextMode);
    setMenuOpen(false);
  };

  const logOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;

      localStorage.removeItem(EVENTS_STORAGE_KEY);
      setEvents([]);
      setSelectedEvent(null);
      setPage('dashboard');
      setMode('admin');
      setMenuOpen(false);
      setDataError('');
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}`
      );
      setUser(null);
      setAuthReady(true);
    } catch (error) {
      window.alert(error.message || 'You could not be logged out. Please try again.');
    }
  };

  const saveSettings = (nextSettings) => {
    setSettings(nextSettings);
    localStorage.setItem('amp-app-settings', JSON.stringify(nextSettings));
  };

  const persistEvent = async (event) => {
    if (!user) throw new Error('Sign in before saving event changes.');
    const savedEvent = await saveEventRecord(user.id, event);
    setEvents((current) => {
      const exists = current.some((item) => String(item.id) === String(savedEvent.id));
      const next = exists
        ? current.map((item) => String(item.id) === String(savedEvent.id) ? savedEvent : item)
        : [savedEvent, ...current];
      localStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    setSelectedEvent(savedEvent);
    return savedEvent;
  };

  const openEvent = (event) => {
    setSelectedEvent(event);
    setSections(getEventSections(event));
    setPage('event');
  };

  const openQuestionnaire = (event = selectedEvent) => {
    if (event) {
      setSelectedEvent(event);
      setSections(getEventSections(event));
    }
    setPage('builder');
  };

  const updateEvent = async (updatedEvent) => {
    const eventWithSections = {
      ...updatedEvent,
      questionnaireSections: Array.isArray(updatedEvent.questionnaireSections)
        ? cloneSections(updatedEvent.questionnaireSections)
        : cloneSections(sections)
    };

    const previousEvent = events.find((event) =>
      String(event.id) === String(updatedEvent.id)
    );
    if (
      previousEvent &&
      String(previousEvent.type || '').toLowerCase() !==
        String(updatedEvent.type || '').toLowerCase() &&
      !hasClientStartedAnswering(updatedEvent)
    ) {
      const { category, template } = findTemplateForEventType(
        getQuestionnaireTemplates(),
        updatedEvent.type
      );

      if (template) {
        Object.assign(eventWithSections, {
          questionnaireTemplateId: template.id,
          questionnaireSections: getClientQuestionnaireSections(
            cloneSections(template.sections),
            updatedEvent.type
          )
        });
      } else {
        Object.assign(eventWithSections, {
          questionnaireTemplateId: '',
          questionnaireSections: []
        });
        if (category) {
          setDataError(`The ${category.label} questionnaire template does not exist. Add it in Questionnaires to assign it.`);
        }
      }
    }

    await persistEvent(eventWithSections);
  };

  const deleteEvents = async (eventIds) => {
    if (!user) throw new Error('Sign in before deleting events.');
    const ids = new Set(eventIds.map(String));
    try {
      await deleteEventRecords(user.id, eventIds);
    } catch (error) {
      setDataError(error.message || 'The event could not be deleted.');
      throw error;
    }
    setDataError('');
    try {
      const fresh = await loadOwnedEvents(user.id);
      setEvents(fresh);
    } catch {
      setEvents((current) => {
        const next = current.filter((event) => !ids.has(String(event.id)));
        localStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }
    setSelectedEvent((current) => current && ids.has(String(current.id)) ? null : current);
    setPage('events');
    setMenuOpen(false);
  };

  const handleEventCreated = async (event, editQuestionnaire = false, questionnaireTemplate = null) => {
    const exactSections = questionnaireTemplate?.sections
      ? getClientQuestionnaireSections(
        cloneSections(questionnaireTemplate.sections),
        questionnaireTemplate.eventType || event.type
      )
      : getEventSections(event);
    const createdEvent = {
      ...event,
      questionnaireTemplateId: questionnaireTemplate?.id || event.questionnaireTemplateId || '',
      questionnaireSections: cloneSections(exactSections),
      shareToken: createClientShareToken(),
      clientAnswers: {}
    };
    try {
      await persistEvent(createdEvent);
      setSections(exactSections);
      setPage(editQuestionnaire ? 'builder' : 'event');
    } catch (error) {
      setDataError(error.message || 'The event could not be saved.');
      throw error;
    }
  };

  const createClientLink = (event) => {
    let token = event.shareToken;
    if (!token) {
      token = createClientShareToken();
      const updated = { ...event, shareToken: token };
      setEvents((current) => current.map((item) =>
        String(item.id) === String(updated.id) ? updated : item
      ));
      setSelectedEvent(updated);
      saveEventRecord(user.id, updated).catch((error) => {
        setDataError(error.message || 'The client link could not be prepared.');
      });
    }
    return `${window.location.origin}${window.location.pathname}#client/${token}`;
  };

  const renderAdminPage = () => {
    if (page === 'dashboard' || page === 'today') {
      return <AdminHome events={events} openEvent={openEvent} onOpenEvents={() => setPage('events')} />;
    }
    if (page === 'events') {
      return (
        <AdminDashboard
          key={monthFilter ? 'month' : 'all'}
          events={events}
          openEvent={openEvent}
          onDeleteEvents={deleteEvents}
          onEventCreated={handleEventCreated}
          monthOnly={monthFilter}
        />
      );
    }
    if (page === 'submissions') {
      return <SubmissionReviewQueue events={events} onOpenEvent={openEvent} />;
    }
    if (page === 'builder') {
      return (
        <QuestionnaireBuilder
          sections={sections}
          setSections={setSections}
          preview={() => changeMode('client')}
          onBack={() => {
            selectedEvent ? setPage('event') : setPage('events');
          }}
          eventName={selectedEvent?.name}
          event={selectedEvent}
        />
      );
    }
    if (page === 'questionnaires') {
      return (
        <QuestionnairesPage
          sections={sections}
          setSections={setSections}
          preview={() => changeMode('client')}
          event={selectedEvent}
          onBack={() => setPage(selectedEvent ? 'event' : 'events')}
          activeTab={questionnaireTab}
          setActiveTab={setQuestionnaireTab}
        />
      );
    }
    if (page === 'event' && selectedEvent) {
      return (
        <EventDetail
          event={selectedEvent}
          onUpdateEvent={updateEvent}
          onDeleteEvent={(eventId) => deleteEvents([eventId])}
          onBack={() => setPage('events')}
          createClientLink={createClientLink}
          onOpenQuestionnaire={() => openQuestionnaire(selectedEvent)}
        />
      );
    }
    if (page === 'settings') {
      return <AppSettings settings={settings} onSave={saveSettings} />;
    }

    return (
      <main className="main-content empty-page">
        <span className="eyebrow">Workspace</span>
        <h1>{page.charAt(0).toUpperCase() + page.slice(1)}</h1>
        <p>This workspace is ready for your next event.</p>
      </main>
    );
  };

  if (!authReady) {
    return (
      <div className="auth-screen">
        <p>Restoring your secure session…</p>
      </div>
    );
  }
  if (mode === 'admin' && !user) return <AuthScreen />;

  const activeClientEvent = clientHash.token ? clientEvent : selectedEvent || clientEvent;
  const clientPreview = mode === 'client' && !clientHash.token;

  return (
    <div
      className="app"
      data-event-date={activeClientEvent ? formatEventDate(activeClientEvent.date) : undefined}
    >
      <AppHeader
        mode={mode}
        setMode={changeMode}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        settings={settings}
        clientPreview={clientPreview}
      />
      {dataError && mode === 'admin' && (
        <div className="event-action-message" role="alert">
          {dataError}
        </div>
      )}
      {mode === 'admin' ? (
        <div className="admin-layout">
          <AdminSidebar
            page={page}
            events={events}
            onOpenMonth={() => {
              setMonthFilter(true);
              setPage('events');
              setMenuOpen(false);
            }}
            onLogOut={logOut}
            setPage={(nextPage) => {
              if (nextPage === 'questionnaires') setQuestionnaireTab('templates');
              if (nextPage === 'events') setMonthFilter(false);
              setPage(nextPage);
              setMenuOpen(false);
            }}
            open={menuOpen}
          />
          {eventsLoading ? (
            <main className="main-content">
              <p>Loading events from Supabase…</p>
            </main>
          ) : (
            renderAdminPage()
          )}
        </div>
      ) : activeClientEvent ? (
        clientHash.token ? (
          <ClientPlannerSync
            key={activeClientEvent.id}
            sections={sections}
            event={activeClientEvent}
            token={clientHash.token}
          />
        ) : (
          <ClientPlanner
            key={activeClientEvent.id}
            sections={sections}
            event={activeClientEvent}
          />
        )
      ) : clientError ? (
        <main className="client-shell client-success-screen">
          <span className="eyebrow">Event unavailable</span>
          <h1>We couldn’t open this event.</h1>
          <p>{clientError}</p>
        </main>
      ) : (
        <main className="client-shell client-success-screen">
          <p>No event is available to preview. Return to the admin workspace and open an event first.</p>
        </main>
      )}
    </div>
  );
}

export default App;